import { isDev } from "api/url-builder";
import { AccuracyLevel, CellRateChangeUnitsType, DynamicType, FluctuationValue, LPerCellUnitsType, RateTimeType, VariableReferenceType, } from "../store/cell-model/cell-model.types";
import { AddLiquidType, BasedOnType, BaseProcessFlowType, BleedType, CellConcentratedExchangeType, CellConcentratedLiquidType, CommonGasType, ConcentrationChangeUnitsType, ConcentrationUnitsType, ConditionOperationType, ConsumptionBasedType, CustomVariableFormulaType, CustomVariableRelativeValueType, CustomVariableTransformationTimestampHumanType, CustomVariableUnitFilterType, CustomVariableValueType, DiluteType, DOControllerStepType, EventValueType, FeedProcessType, FeedType, FillType, GasProcessFlowBasedOnType, GasType, InducerType, InoculationType, InoculumConcentrationUnitsType, InterventionType, LoopConditionType, MassUnitsType, MediaExchangeType, NoUnitsType, OperationType, ParameterTypesCategoryType, PercentageUnitsType, PHChangeUnitsType, PhControllerType, PowerUnitsType, PressureUnitsType, RampType, RateByType, RateChangeCoefficientsType, RateChangeUnitsType, RateCoefficientsType, RateUnitsType, SamplingType, SpecificPowerUnitsType, SpecificRateChangeUnitsType, SpecificRateUnitsType, SpecificStirringUnitsType, StirProcessFlowBasedOnType, StirringUnitsType, TakeSampleType, TemperatureChangeUnitsType, TemperatureUnitsType, TimeChangeType, TimeProfileType, TimeUnitsType, VelocityUnitsType, VolumeUnitsType, WaitType, } from "../store/recipe/recipe.types";
import { CutType, SearchDirection } from "./types";
import { RunsCount, ResultElementSourceType, TableElementColumnViewType, TableElementSourceType, ResultElementParameterType, } from "../store/rs-workspace/rs-workspace.types";
export const N_A = "N/A";
export const EXAMPLE_TAG = "Example";
const microLabel = "<span>&#181;</span>";
export const SUPERSCRIPT_STYLES = {
    fontSize: "0.7em",
    position: "relative",
    top: "calc(-0.3em - 1px)",
};
const SUBSCRIPT_STYLES = {
    ...SUPERSCRIPT_STYLES,
    top: "calc(0.3em - 1px)",
};
export const superscriptValue = (value) => `<span style='font-size: ${SUPERSCRIPT_STYLES.fontSize}; position: ${SUPERSCRIPT_STYLES.position}; top: ${SUPERSCRIPT_STYLES.top};'>${value}</span>`;
export const subscriptValue = (value) => `<span style='font-size: ${SUBSCRIPT_STYLES.fontSize}; position: ${SUBSCRIPT_STYLES.position}; top: ${SUBSCRIPT_STYLES.top};'>${value}</span>`;
export const findDictionaryItemByValue = (dictionary, value, prefix) => {
    if (value === undefined) {
        return;
    }
    const item = dictionary.find(({ value: v }) => v === value);
    const itemPrefix = typeof prefix === "string" ? prefix : prefix ? prefix(item?.value) : undefined;
    const itemToDisplay = item
        ? { ...item, label: `${itemPrefix ? `${itemPrefix} ` : ""}${item.label}` }
        : undefined;
    const defaultLabel = value;
    return (itemToDisplay ?? {
        value,
        label: `${defaultLabel ? `${itemPrefix ? `${itemPrefix} ` : ""}${defaultLabel}` : ""}`,
    });
};
export const getDefaultUnitsPrefix = (units) => {
    switch (units) {
        case InoculumConcentrationUnitsType.MILLION_CELLS_PER_ML:
            return "*";
    }
};
export const getUnitsOptionsWithCategoryNames = (units) => units.reduce((acc, item) => {
    const categoryType = item.find(({ category }) => !!category)?.category;
    if (categoryType) {
        const categoryName = ParameterTypesCategoryName[categoryType];
        const categoryOption = {
            value: categoryType,
            label: categoryName,
            isGroupName: true,
        };
        return [...acc, categoryOption, ...item];
    }
    else {
        return acc;
    }
}, []);
export const baseCalculationsLevelOptions = [
    ...(isDev ? [{ value: RunsCount.Tech3, label: "Tech - 3" }] : []),
    { value: RunsCount.Low, label: "Low - 25" },
    { value: RunsCount.Medium, label: "Medium - 50" },
    { value: RunsCount.High, label: "High - 100" },
];
export const calculationsLevelOptions = [
    { value: RunsCount.None, label: "None - 0" },
    ...baseCalculationsLevelOptions,
];
export const MIXED = "Mixed";
export const totalCalculationsLevelOptions = [
    { label: MIXED, value: MIXED, disabled: true },
    ...calculationsLevelOptions,
];
export const totalStepsCalculationsLevelOptions = [
    { label: MIXED, value: MIXED, disabled: true },
    ...baseCalculationsLevelOptions,
];
// Cell-model
export const dynamicTypes = [
    { value: DynamicType.RANGE, label: DynamicType.RANGE },
    { value: DynamicType.FIXED, label: DynamicType.FIXED },
    { value: DynamicType.DISTRIBUTION, label: DynamicType.DISTRIBUTION },
];
export const dynamicFluctuations = [
    { value: FluctuationValue.NO, label: "No - 0%" },
    { value: FluctuationValue.LOW, label: "Low - 1%" },
    { value: FluctuationValue.MEDIUM, label: "Medium - 5%" },
    { value: FluctuationValue.HIGH, label: "High - 20%" },
    { value: FluctuationValue.EXTREME, label: "Extreme - 100%" },
];
export const dynamicRateTimeTypes = [
    { value: RateTimeType.PER_MIN, label: `min${superscriptValue(-1)}` },
    { value: RateTimeType.PER_HOUR, label: `hr${superscriptValue(-1)}` },
    { value: RateTimeType.PER_DAY, label: `day${superscriptValue(-1)}` },
];
// Recipe
export const addLiquidTypes = [
    { value: AddLiquidType.FILL, label: "Fill" },
    { value: AddLiquidType.INOCULATION, label: "Inoculation" },
    { value: AddLiquidType.INDUCER, label: "Inducer" },
    { value: AddLiquidType.DILUTE, label: "Dilute" },
    { value: AddLiquidType.FEED, label: "Feed" },
];
export const fillTypes = [
    { value: FillType.FILL_TO, label: "To" },
    { value: FillType.FILL_WITH, label: "With" },
];
export const feedTypes = [
    { value: FeedType.AMOUNT_CONSTITUENT, label: "Amount of feed component" },
    { value: FeedType.CONCENTRATION_CONSTITUENT, label: "To concentration of feed component" },
    { value: FeedType.MAINTAIN_CONSTITUENT, label: "To maintain concentration of feed component" },
    { value: FeedType.CELL_DENSITY, label: "To target cell density" },
    { value: FeedType.VOLUME, label: "Add volume" },
    { value: FeedType.TO_VOLUME, label: "Add to volume" },
];
export const feedProcessTypes = [
    { value: FeedProcessType.MAINTAIN, label: "Maintain target cell density" },
    { value: FeedProcessType.ACHIEVE, label: "Achieve target cell density" },
];
export const operationTypes = [
    { value: OperationType.MAINTAIN, label: "Maintain" },
    { value: OperationType.RAMP, label: "Ramp" },
];
export const fromCurrentTypes = [
    { value: false, label: "From zero" },
    { value: true, label: "From current" },
];
export const consumptionBasedTypes = [
    { value: ConsumptionBasedType.LINEAR_EXTRAPOLATION, label: "Linear extrapolation" },
    { value: ConsumptionBasedType.CELL_DENSITY, label: "Cell density (last sampling)" },
];
export const rampTypes = [
    { value: RampType.TOTAL_TIME, label: "Total time" },
    { value: RampType.END_RATE, label: "End rate" },
    { value: RampType.RATE_CHANGE_COEFFICIENT, label: "Rate change coefficient" },
];
export const timeChangeTypes = [
    { value: TimeChangeType.LINEAR, label: "Linear" },
    { value: TimeChangeType.EXPONENTIAL, label: "Exponential" },
];
export const rateByTypes = [
    { value: RateByType.BY_RATE, label: "Rate" },
    { value: RateByType.BY_TIME, label: "Time" },
];
export const timeProfileTypes = [
    { value: TimeProfileType.BOLUS, label: "Bolus (no profile)" },
    { value: TimeProfileType.CONSTANT_RATE, label: "Constant rate" },
    { value: TimeProfileType.RAMP, label: "Ramp" },
];
export const diluteTypes = [
    { value: DiluteType.MAINTAIN, label: "Maintain target cell density" },
    { value: DiluteType.ACHIEVE, label: "Achieve target cell density" },
];
export const inducerTypes = [
    { value: InducerType.VOLUME, label: "Volume" },
    { value: InducerType.CURRENT_CELL_DENSITY, label: "Based on current cell density" },
    { value: InducerType.VOLUME_PER_CELL, label: "Volume per cells" },
];
export const inoculationTypes = [
    { value: InoculationType.TARGET_CELL_DENSITY, label: "Target cell density" },
    { value: InoculationType.TARGET_VOLUME, label: "Add to target volume" },
    { value: InoculationType.VOLUME, label: "Add volume" },
];
export const waitTypes = [
    { value: WaitType.FOR_TIME, label: "For time" },
    { value: WaitType.FOR_EVENT, label: "For event" },
];
export const eventValueTypes = [
    { value: EventValueType.REACHES, label: "Reaches" },
    { value: EventValueType.GOES_OVER, label: "Goes over" },
    { value: EventValueType.GOES_UNDER, label: "Goes under" },
    { value: EventValueType.OUT_OF_RANGE, label: "Goes out of range" },
];
export const pastFormEventValueTypes = [
    { value: EventValueType.REACHES, label: "Reached" },
    { value: EventValueType.GOES_OVER, label: "Gone over" },
    { value: EventValueType.GOES_UNDER, label: "Gone under" },
    { value: EventValueType.OUT_OF_RANGE, label: "Gone out of range" },
];
export const conditionOperationTypes = [
    { value: ConditionOperationType.GREATER_THAN, label: "Greater than" },
    { value: ConditionOperationType.GREATER_THAN_OR_EQUAL, label: "Greater than or equals to" },
    { value: ConditionOperationType.LESS_THAN, label: "Less than" },
    { value: ConditionOperationType.LESS_THAN_OR_EQUAL, label: "Less than or equals to" },
    { value: ConditionOperationType.IN_RANGE, label: "Within range" },
    { value: ConditionOperationType.OUT_OF_RANGE, label: "Out of range" },
];
export const inoculumConcentrationUnits = [
    {
        value: InoculumConcentrationUnitsType.MILLION_CELLS_PER_ML,
        label: `10${superscriptValue(6)} cells mL${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfInoculumConcentration,
    },
    {
        value: InoculumConcentrationUnitsType.CELLS_PER_ML,
        label: `cells mL${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfInoculumConcentration,
    },
];
export const DOControllerStepTypes = [
    {
        value: DOControllerStepType.STIR,
        label: "Stir",
    },
    {
        value: DOControllerStepType.GAS_SUPPLEMENT,
        label: "Gas supplement",
    },
    {
        value: DOControllerStepType.GAS_RATIO,
        label: "Gas ratio",
    },
];
export const PhControllerTypes = [
    {
        value: PhControllerType.PID,
        label: "PID",
    },
    {
        value: PhControllerType.BOLUS,
        label: "Bolus",
    },
];
export const interventionTypes = [
    {
        value: InterventionType.LIQUID,
        label: "Liquid",
    },
    {
        value: InterventionType.GAS,
        label: "Gas",
    },
];
export const loopConditionTypes = [
    {
        value: LoopConditionType.AFTER_TIME,
        label: "After time",
    },
    {
        value: LoopConditionType.ITERATIONS_COUNT,
        label: "After repeats",
    },
    {
        value: LoopConditionType.CONDITION,
        label: "When condition is met",
    },
];
export const takeSampleTypes = [
    {
        value: TakeSampleType.VOLUME_REDUCTION,
        label: "Volume reduction",
    },
    {
        value: TakeSampleType.SAMPLING,
        label: "Sampling",
    },
];
export const samplingTypes = [
    {
        value: SamplingType.BY_VOLUME,
        label: "Based on volume",
    },
    {
        value: SamplingType.BY_CELLS_NUMBER,
        label: "Based on number of cells in sample ",
    },
];
export const mediaExchangeTypes = [
    {
        value: MediaExchangeType.CELL_FREE_LIQUID,
        label: "Sample cell-free liquid",
    },
    {
        value: MediaExchangeType.CELL_CONCENTRATED,
        label: "Sample cell-concentrated liquid",
    },
];
export const cellConcentratedExchangeTypes = [
    {
        value: CellConcentratedExchangeType.BY_VOLUME,
        label: "Based on volume",
    },
    {
        value: CellConcentratedExchangeType.BY_CELL_NUMBER,
        label: "Based on number of cells",
    },
];
export const bleedTypes = [
    {
        value: BleedType.HOLD_CELL_DENSITY,
        label: "Hold cell density constant",
    },
    {
        value: BleedType.HOLD_VOLUME,
        label: "Hold volume constant",
    },
    {
        value: BleedType.REMOVE_CELL_FREE_LIQUID,
        label: "Remove cell-free liquid",
    },
    {
        value: BleedType.REMOVE_CELL_CONCENTRATED_LIQUID,
        label: "Remove cell-concentrated liquid",
    },
];
export const cellConcentratedLiquidTypes = [
    {
        value: CellConcentratedLiquidType.VOLUME,
        label: "Based on volume",
    },
    {
        value: CellConcentratedLiquidType.NUMBER_OF_CELLS,
        label: "Based on number of cell",
    },
];
export const gasTypes = [
    { value: CommonGasType.O2, label: "O2" },
    { value: CommonGasType.CO2, label: "CO2" },
    { value: GasType.N2, label: "N2" },
    { value: GasType.AIR, label: "Air" },
];
// Custom variable
export const customVariableFormulas = [
    { value: CustomVariableFormulaType.Value, label: "None" },
    { value: CustomVariableFormulaType.Sum, label: "Sum" },
    { value: CustomVariableFormulaType.Difference, label: "Difference" },
    { value: CustomVariableFormulaType.Product, label: "Product" },
    { value: CustomVariableFormulaType.Quotient, label: "Quotient" },
    { value: CustomVariableFormulaType.Maximum, label: "Maximum" },
    { value: CustomVariableFormulaType.Minimum, label: "Minimum" },
    { value: CustomVariableFormulaType.Exponential, label: "Exponential" },
    { value: CustomVariableFormulaType.AbsoluteDifference, label: "Absolute difference" },
    { value: CustomVariableFormulaType.ZScore, label: "Z-score" },
    { value: CustomVariableFormulaType.SaturationScore, label: "Saturation-score" },
    { value: CustomVariableFormulaType.LogisticScore, label: "Logistic-score" },
    { value: CustomVariableFormulaType.LogBase10, label: "Log base 10" },
    { value: CustomVariableFormulaType.Ln, label: "Ln" },
    { value: CustomVariableFormulaType.Power, label: "Power" },
];
export const customVariablesListUnitsFilterOptions = [
    { value: CustomVariableUnitFilterType.All, label: "Show all units" },
    { value: CustomVariableUnitFilterType.Regular, label: "Show only regular units" },
    { value: CustomVariableUnitFilterType.Complex, label: "Show only complex units" },
];
export const customVariableValueTypes = [
    {
        value: CustomVariableValueType.Absolute,
        label: "123",
        title: "Absolute value",
    },
    {
        value: CustomVariableValueType.Relative,
        label: "%",
        title: "Relative value",
    },
];
export const customVariableRelativeValueTypes = [
    { value: CustomVariableRelativeValueType.Variable, label: "Variable" },
    { value: CustomVariableRelativeValueType.Parameter, label: "Parameter" },
];
export const customVariableTimestampTypes = [
    { value: CustomVariableTransformationTimestampHumanType.CurrentTime, label: "Offset from current time" },
    { value: CustomVariableTransformationTimestampHumanType.Timestamp, label: "Timestamp configuration" },
];
// Units
export const noUnits = [
    { value: NoUnitsType.NoUnits, label: `No units`, category: ParameterTypesCategoryType.NoUnits },
];
export const timeUnits = [
    { value: TimeUnitsType.Hr, label: "hr", category: ParameterTypesCategoryType.UnitsOfTime },
    { value: TimeUnitsType.Min, label: "min", category: ParameterTypesCategoryType.UnitsOfTime },
    { value: TimeUnitsType.S, label: "s", category: ParameterTypesCategoryType.UnitsOfTime },
];
export const rateCoefficients = [
    {
        value: RateCoefficientsType.Hr1,
        label: `hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.CoefficientsOfRate,
    },
    {
        value: RateCoefficientsType.Min1,
        label: `min${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.CoefficientsOfRate,
    },
    {
        value: RateCoefficientsType.S1,
        label: `s${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.CoefficientsOfRate,
    },
    {
        value: RateCoefficientsType.Day1,
        label: `day${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.CoefficientsOfRate,
    },
];
export const rateChangeCoefficients = [
    {
        value: RateChangeCoefficientsType.Hr2,
        label: `hr${superscriptValue(-2)}`,
        category: ParameterTypesCategoryType.CoefficientsOfRateChange,
    },
    {
        value: RateChangeCoefficientsType.Min2,
        label: `min${superscriptValue(-2)}`,
        category: ParameterTypesCategoryType.CoefficientsOfRateChange,
    },
    {
        value: RateChangeCoefficientsType.S2,
        label: `s${superscriptValue(-2)}`,
        category: ParameterTypesCategoryType.CoefficientsOfRateChange,
    },
];
export const temperatureUnits = [
    { value: TemperatureUnitsType.C, label: "°C", category: ParameterTypesCategoryType.UnitsOfTemperature },
    { value: TemperatureUnitsType.F, label: "°F", category: ParameterTypesCategoryType.UnitsOfTemperature },
];
export const temperatureChangeUnits = [
    {
        value: TemperatureChangeUnitsType.Chr1,
        label: `°C hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfTemperatureChange,
    },
    {
        value: TemperatureChangeUnitsType.Cmin1,
        label: `°C min${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfTemperatureChange,
    },
    {
        value: TemperatureChangeUnitsType.Fhr1,
        label: `°F hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfTemperatureChange,
    },
    {
        value: TemperatureChangeUnitsType.Fmin1,
        label: `°F min${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfTemperatureChange,
    },
];
const baseProcessFlowTypes = [
    { label: "Constant rate", value: BaseProcessFlowType.CONSTANT_RATE },
    { label: "Ramp", value: BaseProcessFlowType.RAMP },
];
export const stirProcessFlowTypes = [
    ...baseProcessFlowTypes,
    { label: "Based on", value: StirProcessFlowBasedOnType.BASED_ON },
];
export const gasProcessFlowTypes = [
    ...baseProcessFlowTypes,
    { label: "Based on kLa", value: GasProcessFlowBasedOnType.BASED_ON_KLA },
];
export const basedOnTypes = [
    { label: "kLa", value: BasedOnType.Kla },
    { label: "PPV", value: BasedOnType.Ppv },
];
export const pHChangeUnits = [
    {
        value: PHChangeUnitsType.PHhr1,
        label: `pH hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfPHChange,
    },
];
export const volumeUnits = [
    {
        value: VolumeUnitsType.MicroL,
        label: `${microLabel}L`,
        category: ParameterTypesCategoryType.UnitsOfVolume,
    },
    { value: VolumeUnitsType.ML, label: "mL", category: ParameterTypesCategoryType.UnitsOfVolume },
    { value: VolumeUnitsType.L, label: "L", category: ParameterTypesCategoryType.UnitsOfVolume },
];
export const rateUnits = [
    {
        value: RateUnitsType.MLmin1,
        label: `mL min${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfRate,
    },
    {
        value: RateUnitsType.MLhr1,
        label: `mL hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfRate,
    },
    {
        value: RateUnitsType.Lmin1,
        label: `L min${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfRate,
    },
    {
        value: RateUnitsType.Lhr1,
        label: `L hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfRate,
    },
];
export const specificRateUnits = [
    {
        value: SpecificRateUnitsType.VVM,
        label: "VVM",
        category: ParameterTypesCategoryType.UnitsOfSpecificRate,
    },
];
export const rateChangeUnits = [
    {
        value: RateChangeUnitsType.MLmin2,
        label: `mL min${superscriptValue(-2)}`,
        category: ParameterTypesCategoryType.UnitsOfRateChange,
    },
    {
        value: RateChangeUnitsType.MLhr2,
        label: `mL hr${superscriptValue(-2)}`,
        category: ParameterTypesCategoryType.UnitsOfRateChange,
    },
    {
        value: RateChangeUnitsType.Lmin2,
        label: `L min${superscriptValue(-2)}`,
        category: ParameterTypesCategoryType.UnitsOfRateChange,
    },
    {
        value: RateChangeUnitsType.Lhr2,
        label: `L hr${superscriptValue(-2)}`,
        category: ParameterTypesCategoryType.UnitsOfRateChange,
    },
];
export const specificRateChangeUnits = [
    {
        value: SpecificRateChangeUnitsType.VVMhr1,
        label: `VVM hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfSpecificRateChange,
    },
];
export const massUnits = [
    { value: MassUnitsType.Microg, label: `${microLabel}g`, category: ParameterTypesCategoryType.UnitsOfMass },
    { value: MassUnitsType.Mg, label: "mg", category: ParameterTypesCategoryType.UnitsOfMass },
    { value: MassUnitsType.G, label: "g", category: ParameterTypesCategoryType.UnitsOfMass },
];
export const concentrationUnits = [
    {
        value: ConcentrationUnitsType.MicrogL1,
        label: `${microLabel}g L${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfConcentration,
    },
    {
        value: ConcentrationUnitsType.MgL1,
        label: `mg L${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfConcentration,
    },
    {
        value: ConcentrationUnitsType.GL1,
        label: `g L${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfConcentration,
    },
];
export const concentrationChangeUnits = [
    {
        value: ConcentrationChangeUnitsType.MicrogL1hr1,
        label: `${microLabel}g L${superscriptValue(-1)} hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfConcentrationChange,
    },
    {
        value: ConcentrationChangeUnitsType.MgL1hr1,
        label: `mg L${superscriptValue(-1)} hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfConcentrationChange,
    },
    {
        value: ConcentrationChangeUnitsType.GL1hr1,
        label: `g L${superscriptValue(-1)} hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfConcentrationChange,
    },
];
export const powerUnits = [
    { value: PowerUnitsType.W, label: "W", category: ParameterTypesCategoryType.UnitsOfPower },
    { value: PowerUnitsType.KW, label: "kW", category: ParameterTypesCategoryType.UnitsOfPower },
];
export const specificPowerUnits = [
    {
        value: SpecificPowerUnitsType.Wm3,
        label: `Wm${superscriptValue(-3)}`,
        category: ParameterTypesCategoryType.UnitsOfSpecificPower,
    },
    {
        value: SpecificPowerUnitsType.KWm3,
        label: `kWm${superscriptValue(-3)}`,
        category: ParameterTypesCategoryType.UnitsOfSpecificPower,
    },
];
export const stirringUnits = [
    { value: StirringUnitsType.Rpm, label: "rpm", category: ParameterTypesCategoryType.UnitsOfStirring },
    { value: StirringUnitsType.Rps, label: "rps", category: ParameterTypesCategoryType.UnitsOfStirring },
];
export const specificStirringUnits = [
    {
        value: SpecificStirringUnitsType.Rpmhr1,
        label: `rpm hr${superscriptValue(-1)}`,
        category: ParameterTypesCategoryType.UnitsOfStirringChange,
    },
];
export const percentageUnits = [
    {
        value: PercentageUnitsType.Percent,
        label: "%",
        category: ParameterTypesCategoryType.UnitsOfPercentage,
    },
];
export const velocityUnits = [
    { value: VelocityUnitsType.Mps, label: "mps", category: ParameterTypesCategoryType.UnitsOfVelocity },
    { value: VelocityUnitsType.Mpmin, label: "mpmin", category: ParameterTypesCategoryType.UnitsOfVelocity },
    { value: VelocityUnitsType.Cmps, label: "cmps", category: ParameterTypesCategoryType.UnitsOfVelocity },
];
export const pressureUnits = [
    { value: PressureUnitsType.MmHg, label: "mm Hg", category: ParameterTypesCategoryType.UnitsOfPressure },
];
export const recipeParameterUnitsType = [
    ...noUnits,
    ...timeUnits,
    ...rateCoefficients,
    ...rateChangeCoefficients,
    ...temperatureUnits,
    ...temperatureChangeUnits,
    ...pHChangeUnits,
    ...volumeUnits,
    ...pressureUnits,
    ...rateUnits,
    ...specificRateUnits,
    ...rateChangeUnits,
    ...specificRateChangeUnits,
    ...massUnits,
    ...concentrationUnits,
    ...inoculumConcentrationUnits,
    ...concentrationChangeUnits,
    ...powerUnits,
    ...specificPowerUnits,
    ...stirringUnits,
    ...specificStirringUnits,
    ...percentageUnits,
    ...velocityUnits,
];
export const lPerCellUnitsType = {
    value: LPerCellUnitsType.LPerCell,
    label: `L cell${superscriptValue(-1)}`,
};
export const cellRateChangeUnitsType = [
    {
        value: CellRateChangeUnitsType.LCellMin1,
        label: `${lPerCellUnitsType.label} min${superscriptValue(-1)}`,
    },
    {
        value: CellRateChangeUnitsType.LCellHr1,
        label: `${lPerCellUnitsType.label} hr${superscriptValue(-1)}`,
    },
    {
        value: CellRateChangeUnitsType.LCellDay1,
        label: `${lPerCellUnitsType.label} day${superscriptValue(-1)}`,
    },
];
export const cellModelParameterUnitsType = [
    ...recipeParameterUnitsType,
    { value: "", label: "" },
    ...cellRateChangeUnitsType,
];
export const variableReferenceTypes = [
    { value: VariableReferenceType.PROCESS, label: "Input variable" },
    { value: VariableReferenceType.FITTING, label: "Fitting variable" },
    {
        value: VariableReferenceType.PROCESS_AND_FITTING,
        label: "Both",
    },
];
export const accuracyLevels = [
    { value: AccuracyLevel.LOW_ACCURACY, label: "Low accuracy" },
    { value: AccuracyLevel.MEDIUM_ACCURACY, label: "Medium accuracy" },
    { value: AccuracyLevel.HIGH_ACCURACY, label: "High accuracy" },
];
//Workspace
export const searchDirections = [
    { value: SearchDirection.Forward, label: "Forward" },
    { value: SearchDirection.Back, label: "Backward" },
];
export const cutTypes = [
    { value: CutType.TimeSeries, label: "Time series" },
    { value: CutType.PointInTime, label: "Single point in time" },
];
//Results
//Table
export const tableElementSourceType = [
    { value: TableElementSourceType.PROCESS_DATA, label: `Process data` },
    { value: TableElementSourceType.SIMULATION, label: `Simulation` },
];
export const resultElementParameterType = [
    { value: ResultElementParameterType.RECIPE, label: "Recipe Parameter" },
    { value: ResultElementParameterType.CELL_MODEL, label: "Cell Model Parameter" },
    { value: ResultElementParameterType.INSIGHT, label: "Insight" },
    { value: ResultElementParameterType.PROCESS_VARIABLE, label: "Process and Custom Variable" },
];
export const tableElementColumnViewType = [
    { value: TableElementColumnViewType.MOST_LIKELY, label: "Most likely" },
    { value: TableElementColumnViewType.AVERAGE, label: "Average " },
    { value: TableElementColumnViewType.OPTIMISTIC, label: "Optimistic" },
    { value: TableElementColumnViewType.PESSIMISTIC, label: "Pessimistic" },
    { value: TableElementColumnViewType.SPREAD, label: "Spread" },
];
export const spaceViewerLineGraphResultElementSourceType = [
    { value: ResultElementSourceType.Simulation, label: "Simulation" },
    { value: ResultElementSourceType.Insight, label: "Insight" },
];
export const resultElementSourceType = [
    { value: ResultElementSourceType.ProcessData, label: "Process Data" },
    { value: ResultElementSourceType.Simulation, label: "Simulation" },
    { value: ResultElementSourceType.Insight, label: "Insight" },
];
export const cellModelParametersGroupName = {
    population: "Population",
    dynamic: "Dynamic",
    dynamicDependencyOnVariable: "Dynamic Dependency on Environment Variable",
    dynamicEffectOnPopulation: "Dynamic Effect on Population",
    dynamicEffectOnVariable: "Dynamic Effect on Environment Variable",
};
export const ParameterTypesCategoryName = {
    [ParameterTypesCategoryType.UnitsOfTime]: "units of time (including mixing time)",
    [ParameterTypesCategoryType.CoefficientsOfRate]: "coefficients of rate (also of kLa change)",
    [ParameterTypesCategoryType.CoefficientsOfRateChange]: "coefficients of rate change (also for kLa change)",
    [ParameterTypesCategoryType.UnitsOfTemperature]: "units of temperature",
    [ParameterTypesCategoryType.UnitsOfTemperatureChange]: "units of temperature change",
    [ParameterTypesCategoryType.UnitsOfPHChange]: "units of pH change",
    [ParameterTypesCategoryType.UnitsOfVolume]: "units of volume",
    [ParameterTypesCategoryType.UnitsOfPressure]: "units of pressure",
    [ParameterTypesCategoryType.UnitsOfRate]: "units of rate (e.g. gassing)",
    [ParameterTypesCategoryType.UnitsOfSpecificRate]: "units of specific rate",
    [ParameterTypesCategoryType.UnitsOfRateChange]: "units of rate change",
    [ParameterTypesCategoryType.UnitsOfSpecificRateChange]: "units of specific rate change",
    [ParameterTypesCategoryType.UnitsOfMass]: "units of mass",
    [ParameterTypesCategoryType.UnitsOfConcentration]: "units of concentration",
    [ParameterTypesCategoryType.UnitsOfConcentrationChange]: "units of concentration change",
    [ParameterTypesCategoryType.UnitsOfPower]: "units of power",
    [ParameterTypesCategoryType.UnitsOfSpecificPower]: "units of specific power",
    [ParameterTypesCategoryType.UnitsOfStirring]: "units of stirring",
    [ParameterTypesCategoryType.UnitsOfStirringChange]: "units of stirring change",
    [ParameterTypesCategoryType.UnitsOfInoculumConcentration]: "units of inoculum concentration",
    [ParameterTypesCategoryType.UnitsOfPercentage]: "units of percentage",
    [ParameterTypesCategoryType.UnitsOfVelocity]: "units of velocity: mps, mpmin, cmps",
    [ParameterTypesCategoryType.NoUnits]: "no units",
    [ParameterTypesCategoryType.Custom]: "custom units",
};
export const parameterUnitsSearchVariants = {
    //Units of time
    [TimeUnitsType.Hr]: [ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTime], "hour", "hr"],
    [TimeUnitsType.Min]: [ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTime], "minute", "min"],
    [TimeUnitsType.S]: [ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTime], "second", "s"],
    //Coefficients of rate
    [RateCoefficientsType.Hr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.CoefficientsOfRate],
        "per hour",
        "h1",
        "h-1",
        "hr1",
        "hr 1",
        "hour-1",
        "hr-1",
        "h^1",
        "h^-1",
        "hr^1",
        "hr^ 1",
        "hour^-1",
        "hr^-1",
    ],
    [RateCoefficientsType.Min1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.CoefficientsOfRate],
        "per min",
        "m1",
        "m-1",
        "min1",
        "min 1",
        "minute-1",
        "min-1",
        "m^1",
        "m^-1",
        "min^1",
        "min^ 1",
        "minute^-1",
        "min^-1",
    ],
    [RateCoefficientsType.S1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.CoefficientsOfRate],
        "per second",
        "sec1",
        "sec-1",
        "s1",
        "s 1",
        "second-1",
        "s-1",
        "sec^1",
        "sec^-1",
        "s^1",
        "s^ 1",
        "second^-1",
        "s^-1",
    ],
    [RateCoefficientsType.Day1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.CoefficientsOfRate],
        "per day",
        "day1",
        "day-1",
        "day 1",
        "day^1",
        "day^-1",
        "day^ 1",
    ],
    //Coefficients of rate change
    [RateChangeCoefficientsType.Hr2]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.CoefficientsOfRateChange],
        "per square hour",
        "hr-2",
        "hr^-2",
    ],
    [RateChangeCoefficientsType.Min2]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.CoefficientsOfRateChange],
        "per square min",
        "min-2",
        "min^-2",
    ],
    [RateChangeCoefficientsType.S2]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.CoefficientsOfRateChange],
        "per square second",
        "s-2",
        "s^-2",
    ],
    //Units of temperature
    [TemperatureUnitsType.C]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTemperature],
        "Celsius",
        "C",
    ],
    [TemperatureUnitsType.F]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTemperature],
        "Fahrenheit",
        "F",
    ],
    //Units of temperature change
    [TemperatureChangeUnitsType.Chr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTemperatureChange],
        "Celsius per hour",
        "C hr-1",
        "C per hr-1",
        "C hr^-1",
        "C per hr^-1",
    ],
    [TemperatureChangeUnitsType.Cmin1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTemperatureChange],
        "Celsius per minute",
        "C min-1",
        "C per min-1",
        "C min^-1",
        "C per min^-1",
    ],
    [TemperatureChangeUnitsType.Fhr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTemperatureChange],
        "Fahrenheit per hour",
        "F hr-1",
        "F hr^-1",
        "F per hr-1",
        "F per hr^-1",
    ],
    [TemperatureChangeUnitsType.Fmin1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfTemperatureChange],
        "Fahrenheit per minute",
        "F min-1",
        "F per min-1",
        "F min^-1",
        "F per min^-1",
    ],
    //Units of pH change
    [PHChangeUnitsType.PHhr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfPHChange],
        "pH per hour",
        "pondus Hydrogenii per hour",
        "pH hr-1",
        "ph per hr-1",
        "pH hr^-1",
        "ph per hr^-1",
    ],
    // Units of pressure
    [PressureUnitsType.MmHg]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfPressure],
        "millimeter of mercury",
        "mm Hg",
        "mm",
        "Hg",
    ],
    //Units of volume
    [VolumeUnitsType.MicroL]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfVolume],
        "microliter",
        "microlitre",
        "micro liter",
        "micro litre",
        "mliter",
        "mlitre",
        "micliter",
        "miclitre",
        "mic liter",
        "mic litre",
        "mL",
    ],
    [VolumeUnitsType.ML]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfVolume],
        "milliliter",
        "millilitre",
        "milli liter",
        "milli litre",
        "mliter",
        "mlitre",
        "milliter",
        "millitre",
        "mil liter",
        "mil litre",
        "mL",
    ],
    [VolumeUnitsType.L]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfVolume],
        "L",
        "Liter",
        "Litre",
    ],
    //Units of rate
    [RateUnitsType.MLmin1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRate],
        "milliliter per minute",
        "millilitre per minute",
        "milliliter per min",
        "mL min-1",
        "ml per min-1",
        "mL min^-1",
        "ml per min^-1",
    ],
    [RateUnitsType.MLhr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRate],
        "milliliter per hour",
        "millilitre per hour",
        "milliliter per hr",
        "millilitre per hr",
        "mL hr-1",
        "ml per hour-1",
        "mL hr^-1",
        "ml per hour^-1",
    ],
    [RateUnitsType.Lmin1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRate],
        "Liter per minute",
        "Litre per minute",
        "Liter per min",
        "Litre per min",
        "L min-1",
        "L min^-1",
        "Lmin-1",
        "Lmin^-1",
        "L per min-1",
        "L per min^-1",
    ],
    [RateUnitsType.Lhr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRate],
        "Liter per hour",
        "Litre per hour",
        "Liter per hr",
        "Litre per hr",
        "L hr-1",
        "L hr^-1",
        "Lhr-1,Lhr^-1",
        "L per hr-1",
        "L per hr^-1",
    ],
    //Units of specific rate
    [SpecificRateUnitsType.VVM]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfSpecificRate],
        "volume per volume per minute",
        "VVM",
    ],
    //Units of rate change
    [RateChangeUnitsType.MLmin2]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRateChange],
        "milliliter per square minute",
        "millilitre per square minute",
        "mL min-2",
        "ml per min-2",
        "mL min^-2",
        "ml per min^-2",
    ],
    [RateChangeUnitsType.MLhr2]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRateChange],
        "milliliter per square hour",
        "millilitre per square hour",
        "mL hr-2",
        "mLhr-2",
        "ml per hr-2",
        "mL hr^-2",
        "mLhr^-2",
        "ml per hr^-2",
    ],
    [RateChangeUnitsType.Lmin2]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRateChange],
        "Liter per square minute",
        "Litre per square minute",
        "L min-2",
        "Lmin-2",
        "L per min-2",
        "L min^-2",
        "Lmin^-2",
        "L per min^-2",
    ],
    [RateChangeUnitsType.Lhr2]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfRateChange],
        "Liter per square hour",
        "Litre per square hour",
        "L hr-2",
        "Lhr-2",
        "L per hr-2",
        "L hr^-2",
        "Lhr^-2",
        "L per hr^-2",
    ],
    //Units of specific rate change
    [SpecificRateChangeUnitsType.VVMhr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfSpecificRateChange],
        "volume per volume per minute",
        "VVM hr-1",
        "VVM per hr-1",
        "VVM hr^-1",
        "VVM per hr^-1",
    ],
    //Units of mass
    [MassUnitsType.Microg]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfMass],
        "microgram",
        "mgram",
        "mg",
    ],
    [MassUnitsType.Mg]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfMass],
        "milligram",
        "mgram",
        "mg",
    ],
    [MassUnitsType.G]: [ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfMass], "gram", "g"],
    //Units of concentration
    [ConcentrationUnitsType.MicrogL1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfConcentration],
        "microgram pre Liter",
        "microgram pre Litre mg L-1",
        "mgL-1",
        "mg per L-1",
        "microgram pre Litre mg L^-1",
        "mgL^-1",
        "mg per L^-1",
    ],
    [ConcentrationUnitsType.MgL1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfConcentration],
        "milligram per Liter",
        "milligram per Litre",
        "mg L-1",
        "mg per L-1",
        "mg L^-1",
        "mg per L^-1",
    ],
    [ConcentrationUnitsType.GL1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfConcentration],
        "gram per Liter",
        "gram per Litre",
        "g L-1",
        "gL-1",
        "g per L-1,g L^-1",
        "gL^-1",
        "g per L^-1",
    ],
    //Units of concentration change
    [ConcentrationChangeUnitsType.MicrogL1hr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfConcentrationChange],
        "microgram pre Liter per hour",
        "microgram pre Litre per hour",
        "mg L-1 hr-1",
        "mgL-1hr-1",
        "mg per L per hr-1",
        "mg L^-1 hr^-1",
        "mgL^-1hr^-1",
        "mg per L per hr^-1",
    ],
    [ConcentrationChangeUnitsType.MgL1hr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfConcentrationChange],
        "milligram per Liter per hour",
        "milligram per Litre per hour",
        "mg L-1 hr-1",
        "mgL-1hr-1",
        "mg per L per hr-1",
        "mg L^-1 hr^-1",
        "mgL^-1hr^-1",
        "mg per L per hr^-1",
    ],
    [ConcentrationChangeUnitsType.GL1hr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfConcentrationChange],
        "gram per Liter per hour",
        "gram per Litre per hour",
        "g L-1 hr-1",
        "gL-1hr-1",
        "g per L per hr-1",
        "g L^-1 hr^-1",
        "gL^-1hr^-1",
        "g per L per hr^-1",
    ],
    //Units of power
    [PowerUnitsType.W]: [ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfPower], "watt", "W"],
    [PowerUnitsType.KW]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfPower],
        "kilowatt",
        "kW",
    ],
    //Units of specific power
    [SpecificPowerUnitsType.Wm3]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfSpecificPower],
        "Watt per cubic minute",
        "Wm-3",
        "Wm^-3",
    ],
    [SpecificPowerUnitsType.KWm3]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfSpecificPower],
        "kilowatt per cubic minute",
        "kWm-3",
        "kW per m-3",
        "kWm^-3",
        "kW per m^-3",
    ],
    //Units of stirring
    [StirringUnitsType.Rpm]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfStirring],
        "revolutions per minute",
        "rpm",
    ],
    [StirringUnitsType.Rps]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfStirring],
        "revolutions per second",
        "rps",
    ],
    //Units of stirring change
    [SpecificStirringUnitsType.Rpmhr1]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfStirringChange],
        "revolutions per hour",
        "rpm hr-1",
        "rpmhr-1",
        "rpm per hr-1",
        "rpm hr^-1",
        "rpmhr^-1",
        "rpm per hr^-1",
    ],
    //Inoculum concentration units type
    [InoculumConcentrationUnitsType.CELLS_PER_ML]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfInoculumConcentration],
        "Cells Ml-1",
        "CellsPerMl",
    ],
    [InoculumConcentrationUnitsType.MILLION_CELLS_PER_ML]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfInoculumConcentration],
        "MillionCellsPerMl",
        "MCells mL-1",
    ],
    //Percentage units type
    [PercentageUnitsType.Percent]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfPercentage],
        "percentage",
        "%",
    ],
    //Velocity units type
    [VelocityUnitsType.Mps]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfVelocity],
        "Meter per second",
        "Metre per second",
        "m per s",
        "m per sec",
        "mpsec",
    ],
    [VelocityUnitsType.Mpmin]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfVelocity],
        "Meter per minute",
        "Metre per minute",
        "m per m",
        "m per min",
        "mpmin",
    ],
    [VelocityUnitsType.Cmps]: [
        ParameterTypesCategoryName[ParameterTypesCategoryType.UnitsOfVelocity],
        "Centimeter per second",
        "Centimetre per second",
        "cm per s",
        "cm per sec",
        "cmpsec",
    ],
    //No units
    [NoUnitsType.NoUnits]: [],
};
